import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  SITE_NAME,
  LOCALITY,
  AREA,
  CITY,
  DEFAULT_TITLE,
  DEFAULT_DESCRIPTION,
  SEO_KEYWORDS,
  PHONE_DISPLAY,
  EMAIL,
  getLocalBusinessJsonLd,
} from '../seo/seoConstants';

function upsertMeta(attrSelector, key, content) {
  if (content === undefined || content === null) return;
  let el = document.querySelector(`meta[${attrSelector}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attrSelector, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', String(content));
}

function getMetaForPath(pathname) {
  const baseDesc = DEFAULT_DESCRIPTION;
  const routes = {
    '/': {
      title: DEFAULT_TITLE,
      description: baseDesc,
    },
    '/about': {
      title: `About ${SITE_NAME} | Builders in ${LOCALITY}, ${CITY}`,
      description: `${SITE_NAME}, based near ${LOCALITY}, ${CITY}, has delivered 100+ construction and building projects across South ${CITY} since 2010. Learn about our civil contracting and residential expertise.`,
    },
    '/contact': {
      title: `Contact ${SITE_NAME} | ${LOCALITY}, ${CITY} Construction`,
      description: `Contact ${SITE_NAME} for home construction, commercial builds, and renovations in ${LOCALITY}, ${AREA}, and ${CITY}. Call ${PHONE_DISPLAY} or email ${EMAIL}.`,
    },
    '/sale': {
      title: `Sale Projects | ${SITE_NAME} ${CITY}`,
      description: `Browse completed and available projects from ${SITE_NAME}, construction company serving ${LOCALITY} and ${CITY}. Quality builds for buyers in Tamil Nadu.`,
    },
    '/projects': {
      title: `Our Projects | Construction Portfolio ${LOCALITY}, ${CITY}`,
      description: `Explore residential and construction projects by ${SITE_NAME} near ${LOCALITY} and ${CITY}. Expert builders for homes and developments across South Chennai.`,
    },
  };

  if (routes[pathname]) {
    return routes[pathname];
  }
  if (pathname.startsWith('/saleproject/')) {
    return {
      title: `Project details | ${SITE_NAME} ${CITY}`,
      description: `Detailed sale project information from ${SITE_NAME}, builders in ${LOCALITY}, ${CITY}.`,
    };
  }
  if (pathname.startsWith('/rentproject/')) {
    return {
      title: `Rental project | ${SITE_NAME} ${CITY}`,
      description: `Rental project details from ${SITE_NAME}, construction and real estate in ${LOCALITY}, ${CITY}.`,
    };
  }
  return { title: DEFAULT_TITLE, description: baseDesc };
}

export default function SeoHead() {
  const { pathname } = useLocation();
  const meta = useMemo(() => getMetaForPath(pathname), [pathname]);

  useEffect(() => {
    document.title = meta.title;
    upsertMeta('name', 'description', meta.description);
    upsertMeta('name', 'keywords', SEO_KEYWORDS);
    upsertMeta('property', 'og:title', meta.title);
    upsertMeta('property', 'og:description', meta.description);
    upsertMeta('property', 'og:type', 'website');
    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', meta.title);
    upsertMeta('name', 'twitter:description', meta.description);
    upsertMeta('name', 'geo.region', 'IN-TN');
    upsertMeta('name', 'geo.placename', `${LOCALITY}, ${CITY}`);
  }, [meta]);

  useEffect(() => {
    const canonical = window.location.href.split('?')[0];
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'canonical';
      document.head.appendChild(link);
    }
    link.href = canonical;
    upsertMeta('property', 'og:url', canonical);
  }, [pathname]);

  useEffect(() => {
    const origin = window.location.origin || '';
    const payload = getLocalBusinessJsonLd(origin);
    let script = document.getElementById('seo-ld-business');
    if (!script) {
      script = document.createElement('script');
      script.id = 'seo-ld-business';
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(payload);
  }, []);

  return null;
}
